import React, { useMemo } from 'react';

function VedicChart({ chartData }) {
  const getPlanetSymbol = (planetName) => {
    const symbols = {
      'SUN': '☉', 'MOON': '☽', 'MERCURY': '☿', 'VENUS': '♀', 'MARS': '♂',
      'JUPITER': '♃', 'SATURN': '♄', 'RAHU': '☊', 'KETU': '☋'
    };
    return symbols[planetName] || planetName.substring(0, 1);
  };

  const getSignAbbrev = (sign) => {
    const abbrev = {
      'Aries': 'Ar', 'Taurus': 'Ta', 'Gemini': 'Ge', 'Cancer': 'Ca',
      'Leo': 'Le', 'Virgo': 'Vi', 'Libra': 'Li', 'Scorpio': 'Sc',
      'Sagittarius': 'Sa', 'Capricorn': 'Cp', 'Aquarius': 'Aq', 'Pisces': 'Pi'
    };
    return abbrev[sign] || '??';
  };

  const getSignColor = (sign) => {
    const colors = {
      'Aries': '#FF6B6B', 'Taurus': '#4ECDC4', 'Gemini': '#FFE66D',
      'Cancer': '#95E1D3', 'Leo': '#FFA502', 'Virgo': '#26C281',
      'Libra': '#FF69B4', 'Scorpio': '#6A0572', 'Sagittarius': '#4169E1',
      'Capricorn': '#8B4513', 'Aquarius': '#00CED1', 'Pisces': '#BA55D3'
    };
    return colors[sign] || '#999';
  };

  // Calculate which planets are in which houses (WHOLE SIGN - traditional Vedic)
  const planetsInHouses = useMemo(() => {
    const houses = {};
    for (let i = 1; i <= 12; i++) {
      houses[i] = [];
    }

    const ascSignIndex = chartData?.houses?.ascendant?.signIndex ?? 0;

    Object.entries(chartData?.planets || {}).forEach(([planetKey, planet]) => {
      if (planet && planet.signIndex !== undefined) {
        // WHOLE SIGN: House number = (planet's sign - ascendant sign + 12) % 12 + 1
        const houseNumber = ((planet.signIndex - ascSignIndex + 12) % 12) + 1;

        houses[houseNumber].push({
          name: planetKey,
          symbol: getPlanetSymbol(planetKey),
          sign: planet.sign,
          degree: planet.degree,
          minute: planet.minute
        });
      }
    });

    return houses;
  }, [chartData]);

  // Get all 12 houses with their signs
  const allHouses = useMemo(() => {
    const ascSign = chartData.houses.ascendant?.signIndex || 0;
    const houses = [];
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
                   'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

    for (let i = 0; i < 12; i++) {
      const signIndex = (ascSign + i) % 12;
      houses.push({
        number: i + 1,
        sign: signs[signIndex],
        signIndex: signIndex
      });
    }

    return houses;
  }, [chartData]);

  if (!chartData || !chartData.planets || !chartData.houses) {
    return <div className="text-white/60 text-center py-8 text-sm">Chart data unavailable</div>;
  }

  return (
    <div className="w-full space-y-6">
      {/* RASI CHART (D1) - 12 House Grid */}
      <div>
        <h3 className="text-sm font-bold text-white mb-3">Rasi Chart (D1) - Birth Chart Grid</h3>

        {/* 4x3 House Grid Layout (Traditional Vedic format) */}
        <div className="grid grid-cols-3 gap-1 bg-slate-950 p-4 rounded border border-slate-700">
          {/* Row 1: Houses 7, 8, 9 */}
          <HouseCell houseNum={7} house={allHouses[6]} planetsInHouse={planetsInHouses[7]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={8} house={allHouses[7]} planetsInHouse={planetsInHouses[8]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={9} house={allHouses[8]} planetsInHouse={planetsInHouses[9]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />

          {/* Row 2: Houses 6, CENTER (ASCENDANT), 10 */}
          <HouseCell houseNum={6} house={allHouses[5]} planetsInHouse={planetsInHouses[6]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-2 border-purple-500/80 rounded p-2 flex flex-col items-center justify-center min-h-24">
            <p className="text-white/60 text-[10px] uppercase tracking-widest font-semibold">Ascendant</p>
            <p className="text-white text-lg font-bold">{chartData.houses.ascendant?.sign || '?'}</p>
            <p className="text-purple-300 text-[10px]">{chartData.houses.ascendant?.degree}°{chartData.houses.ascendant?.minute}'</p>
          </div>
          <HouseCell houseNum={10} house={allHouses[9]} planetsInHouse={planetsInHouses[10]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />

          {/* Row 3: Houses 5, 4, 11 */}
          <HouseCell houseNum={5} house={allHouses[4]} planetsInHouse={planetsInHouses[5]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={4} house={allHouses[3]} planetsInHouse={planetsInHouses[4]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={11} house={allHouses[10]} planetsInHouse={planetsInHouses[11]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />

          {/* Row 4: Houses 3, 2, 1 */}
          <HouseCell houseNum={3} house={allHouses[2]} planetsInHouse={planetsInHouses[3]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={2} house={allHouses[1]} planetsInHouse={planetsInHouses[2]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <HouseCell houseNum={1} house={allHouses[0]} planetsInHouse={planetsInHouses[1]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />

          {/* Row 5: House 12 (spans bottom) */}
          <HouseCell houseNum={12} house={allHouses[11]} planetsInHouse={planetsInHouses[12]} getSignColor={getSignColor} getSignAbbrev={getSignAbbrev} />
          <div></div>
          <div></div>
        </div>
      </div>

      {/* PLANETS LEGEND */}
      <div>
        <h3 className="text-sm font-bold text-white mb-3">Planetary Positions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(chartData.planets).map(([key, planet]) => {
            if (!planet || !planet.sign) return null;

            const houseNum = Object.entries(planetsInHouses).find(([_, planets]) =>
              planets.some(p => p.name === key)
            )?.[0] || '?';

            return (
              <div key={key} className="bg-slate-800/60 border border-slate-700 rounded p-3 text-xs">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl font-bold text-yellow-300">{getPlanetSymbol(key)}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-white text-sm">{key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()}</p>
                    <p className="text-white/50 text-xs">H{houseNum}</p>
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded p-1.5">
                  <p className="text-white font-mono">{planet.sign}</p>
                  <p className="text-white/60">{planet.degree}° {planet.minute}'</p>
                  {planet.nakshatra && (
                    <p className="text-white/40 text-[9px] mt-1">{planet.nakshatra.name}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* INFO */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 text-xs text-blue-200">
        <p>💡 <strong>D1 (Rasi) Chart:</strong> This is your birth chart showing all 12 houses and planetary positions.
        The ascendant (rising sign) is at the center. Each planet's position reveals its influence on different life areas.</p>
      </div>
    </div>
  );
}

// Helper: Get short planet name (3-letter abbreviation)
function getPlanetShortName(planetKey) {
  const names = {
    'SUN': 'Sun',
    'MOON': 'Moon',
    'MERCURY': 'Merc',
    'VENUS': 'Venus',
    'MARS': 'Mars',
    'JUPITER': 'Jup',
    'SATURN': 'Sat',
    'RAHU': 'Rahu',
    'KETU': 'Ketu'
  };
  return names[planetKey] || planetKey;
}

// House Cell Component
function HouseCell({ houseNum, house, planetsInHouse, getSignColor, getSignAbbrev }) {
  const isAngular = [1, 4, 7, 10].includes(houseNum);

  return (
    <div
      className={`rounded p-2 min-h-28 border-2 flex flex-col transition ${
        isAngular
          ? 'border-purple-500/60 bg-purple-900/25'
          : 'border-slate-700 bg-slate-800/30'
      }`}
    >
      {/* House Header */}
      <div className="flex items-center justify-between mb-1">
        <p className="text-white font-bold text-xs">H{houseNum}</p>
        <p className="text-[10px] uppercase tracking-wider font-bold"
           style={{ color: getSignColor(house.sign) }}>
          {getSignAbbrev(house.sign)}
        </p>
      </div>

      {/* Planets - With Name + Symbol + Degree */}
      <div className="flex-1 flex flex-col gap-0.5 items-stretch">
        {planetsInHouse && planetsInHouse.length > 0 ? (
          planetsInHouse.map((planet, idx) => (
            <div key={idx} className="bg-slate-700/60 rounded px-1.5 py-0.5 border border-slate-600 flex items-center gap-1.5">
              <span className="text-yellow-300 text-sm font-bold leading-none">{planet.symbol}</span>
              <span className="text-white text-[10px] font-semibold flex-1">{getPlanetShortName(planet.name)}</span>
              <span className="text-white/60 text-[10px] font-mono">{planet.degree}°</span>
            </div>
          ))
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-white/20 text-xs">—</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VedicChart;
